/**
 * NUDGE-01 — "Close-outs Not Sent" on Today (Seth 2026-10-07: "the office
 * can send the post-install email when ready. If it hasn't been sent maybe
 * 5 days after install, it should just pop up as something on the Today
 * tab").
 *
 * Pegasus install jobs whose visits are ALL finished, where the last visit
 * wrapped 5+ days ago, and no close-out recap has ever been queued or sent.
 * One click lands on the job's Post-Install board. Renders nothing on a
 * clean day. ESC-era installs are handled manually and never appear here;
 * test jobs are excluded.
 */
import { useQuery } from "@tanstack/react-query";
import { MailWarning, ArrowRight } from "lucide-react";
import { supabase } from "@/lib/supabase";
import { useNavigateWithBack } from "@/lib/navBack";

const NUDGE_DAYS = 5;
const SHOW = 8;

interface Nudge { id: string; title: string | null; customer: string; days: number }

export function useCloseoutNudges() {
  return useQuery({
    queryKey: ["closeout_nudges"],
    staleTime: 5 * 60_000,
    queryFn: async (): Promise<Nudge[]> => {
      const cutoffIso = new Date(Date.now() - NUDGE_DAYS * 864e5).toISOString();
      const floorIso = new Date(Date.now() - 90 * 864e5).toISOString();
      // Newest completed visit per job (recent window keeps this cheap).
      const { data: done } = await supabase.from("pegasus_dispatch")
        .select("job_id, updated_at")
        .eq("status", "completed").is("deleted_at", null)
        .not("job_id", "is", null).gt("updated_at", floorIso).limit(500);
      const newest = new Map<string, string>();
      for (const d of (done ?? []) as { job_id: string; updated_at: string }[]) {
        const prev = newest.get(d.job_id);
        if (!prev || d.updated_at > prev) newest.set(d.job_id, d.updated_at);
      }
      const aged = [...newest.entries()].filter(([, ts]) => ts <= cutoffIso).map(([id]) => id);
      if (!aged.length) return [];
      // A visit still open means the install isn't finished — no nudge yet.
      const { data: open } = await supabase.from("pegasus_dispatch")
        .select("job_id").in("job_id", aged).is("deleted_at", null)
        .not("status", "in", "(completed,canceled,cancelled)");
      const openSet = new Set(((open ?? []) as { job_id: string }[]).map((o) => o.job_id));
      const finished = aged.filter((id) => !openSet.has(id));
      if (!finished.length) return [];
      const { data: jobs } = await supabase.from("pegasus_job")
        .select("id, title, custno, customer_name, source_dispatch, is_test").in("id", finished);
      const rows = ((jobs ?? []) as { id: string; title: string | null; custno: string | null; customer_name: string | null; source_dispatch: string | null; is_test: boolean | null }[])
        .filter((j) => !j.is_test);
      if (!rows.length) return [];
      const refs = rows.map((j) => j.source_dispatch ?? `job-${j.id}`);
      const { data: sends } = await supabase.from("pegasus_outbox")
        .select("trigger_ref, status").eq("automation_key", "post_install_recap").in("trigger_ref", refs);
      const handled = new Set(((sends ?? []) as { trigger_ref: string; status: string }[])
        .filter((s) => ["held", "queued", "sent"].includes(s.status)).map((s) => s.trigger_ref));
      return rows
        .filter((j) => !handled.has(j.source_dispatch ?? `job-${j.id}`))
        .map((j) => ({
          id: j.id,
          title: j.title,
          customer: j.customer_name ?? j.custno ?? "—",
          days: Math.max(NUDGE_DAYS, Math.floor((Date.now() - new Date(newest.get(j.id)!).getTime()) / 864e5)),
        }))
        .sort((a, b) => b.days - a.days);
    },
  });
}

export function CloseoutNudgesPanel() {
  const navigate = useNavigateWithBack();
  const { data: rows } = useCloseoutNudges();
  if (!rows || rows.length === 0) return null;
  return (
    <div className="rounded-2xl border border-amber-200 bg-amber-50/50 p-4 shadow-sm">
      <p className="mb-2 text-sm font-semibold text-slate-900">
        {rows.length} install close-out email{rows.length === 1 ? "" : "s"} not sent
        <span className="ml-2 font-normal text-slate-500">finished {NUDGE_DAYS}+ days ago</span>
      </p>
      <ul className="divide-y divide-amber-100">
        {rows.slice(0, SHOW).map((r) => (
          <li key={r.id} className="flex flex-wrap items-center gap-x-3 gap-y-1 py-2 text-sm">
            <MailWarning className="h-4 w-4 shrink-0 text-amber-500" />
            <span className="min-w-0 max-w-[240px] truncate font-medium text-slate-900">{r.customer}</span>
            {r.title && <span className="min-w-0 max-w-[260px] truncate text-xs text-slate-500">{r.title}</span>}
            <span className="text-xs font-semibold text-amber-700">{r.days} days</span>
            <button type="button" onClick={() => navigate(`/jobs/${r.id}?tab=post`)}
              className="ml-auto inline-flex shrink-0 items-center gap-1 rounded-lg bg-white px-2.5 py-1 text-xs font-bold text-amber-800 ring-1 ring-amber-200 transition-colors hover:bg-amber-100">
              Open Close-Out <ArrowRight className="h-3 w-3" />
            </button>
          </li>
        ))}
      </ul>
      {rows.length > SHOW && (
        <p className="mt-1.5 text-center text-xs text-slate-400">+ {rows.length - SHOW} more</p>
      )}
    </div>
  );
}
